import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useRoute } from "wouter";
import Header from "@/components/Header";
import OrderChat from "@/components/OrderChat";
import { trackEvent } from "@/lib/analytics";
import { LiveDeliveryTracker } from "@/components/LiveDeliveryTracker";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Loader2,
  MapPin,
  MessageCircle,
  Package,
  Phone,
  Receipt,
  Truck,
  Star,
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { formatOrderStatus, formatScheduledFor } from "@/lib/orderStatus";
import { stripDemoMarker } from "@/lib/demo";
import { calculateMultiSellerFees } from "@shared/fees";
import { formatWeight } from "@shared/weight";
import { ServiceFeeInfo } from "@/components/ServiceFeeInfo";
import type {
  Order,
  OrderFulfillmentSelection,
  DeliveryRequestStop,
  CustomResponse,
} from "@shared/schema";

type ViewerRole = "buyer" | "seller" | "driver";

type DetailLine = {
  id: string;
  orderId: string;
  itemId: string;
  itemName: string;
  sellerName: string;
  unitPrice: string;
  qty: number;
  sellerId: string | null;
  imageUrl: string | null;
  requestText: string | null;
  requestImageUrls?: string[] | null;
  customResponses: CustomResponse[] | null;
  unitWeightCentiLb?: number | null;
};

type DetailSeller = {
  id: string;
  displayName: string;
  profileImageUrl: string | null;
  isFoundingMaker: boolean;
};

type DetailBuyer = {
  id: string;
  displayName: string;
  profileImageUrl: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
};

type DetailDriver = {
  id: string;
  displayName: string;
  profileImageUrl: string | null;
};

type DetailDeliveryRequest = {
  id: string;
  status: string;
  acceptedByUserId: string | null;
  scheduledFor: string | null;
  deliverByDate: string | null;
  completedAt: string | null;
  deliveryFee: string | null;
  buyerAddressLine1: string | null;
  buyerAddressLine2: string | null;
  buyerCity: string | null;
  buyerState: string | null;
  buyerLat: string | null;
  buyerLng: string | null;
  stops: DeliveryRequestStop[];
  driver: DetailDriver | null;
};

type OrderDetailResponse = {
  order: Order & { fulfillmentSelections: OrderFulfillmentSelection[] };
  lines: DetailLine[];
  sellers: DetailSeller[];
  buyer: DetailBuyer;
  deliveryRequest: DetailDeliveryRequest | null;
  viewerRole: ViewerRole;
};

function timeAgo(iso: string | Date | null | undefined): string {
  if (!iso) return "";
  const date = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function statusBadgeClass(status: string | null | undefined): string {
  const s = String(status || "").toLowerCase();
  if (s === "delivered" || s === "picked_up") {
    return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400";
  }
  if (s === "cancelled" || s === "refunded") {
    return "bg-muted text-muted-foreground";
  }
  if (s === "cancel_requested") {
    return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400";
  }
  return "bg-primary/10 text-primary";
}

function isDeliveryMethod(method: string | null | undefined): boolean {
  return method === "community_delivery" || method === "seller_delivery";
}

type CtaSpec = {
  label: string;
  onClick: () => void;
  hint?: string;
  variant?: "default" | "destructive";
} | null;

function buildCta(args: {
  viewerRole: ViewerRole;
  status: string;
  isDelivery: boolean;
  isCommunityDelivery: boolean;
  hasReview: boolean;
  onStatusChange: (status: string) => void;
  onGoToDeliveries: () => void;
  onOpenReview: () => void;
}): CtaSpec {
  const { viewerRole, status, isDelivery, isCommunityDelivery, hasReview } = args;

  // A pending order is an unpaid, abandoned checkout. Let the buyer (or a
  // seller on the order) dismiss it so it doesn't sit stuck forever.
  if (status === "pending" && (viewerRole === "buyer" || viewerRole === "seller")) {
    return {
      label: "Cancel order",
      variant: "destructive",
      onClick: () => {
        if (
          window.confirm(
            "This order was never paid for. Cancel it? This can't be undone.",
          )
        ) {
          args.onStatusChange("cancelled");
        }
      },
    };
  }

  if (viewerRole === "buyer") {
    if (!isDelivery && status === "ready_for_pickup") {
      return {
        label: "I'm picking up",
        onClick: () => args.onStatusChange("picked_up"),
      };
    }
    if ((status === "picked_up" || status === "delivered") && !hasReview) {
      return { label: "Rate your order", onClick: args.onOpenReview };
    }
    return null;
  }

  if (viewerRole === "seller") {
    if (status === "paid") {
      return {
        label: "Start preparing",
        onClick: () => args.onStatusChange("preparing"),
      };
    }
    if (status === "preparing") {
      // On community-delivery orders the assigned driver handles pickup and
      // drop-off behind a GPS + proof-photo gate, so the seller's role ends at
      // marking the order ready. Only seller-driven delivery gets the handoff.
      return isDelivery && !isCommunityDelivery
        ? {
            label: "Hand off to driver",
            onClick: () => args.onStatusChange("out_for_delivery"),
          }
        : {
            label: "Mark ready for pickup",
            onClick: () => args.onStatusChange("ready_for_pickup"),
          };
    }
    return null;
  }

  if (viewerRole === "driver") {
    if (status === "out_for_delivery") {
      return { label: "Continue delivery", onClick: args.onGoToDeliveries };
    }
    return null;
  }

  return null;
}

export default function OrderDetail() {
  const [, routeParams] = useRoute("/orders/:id");
  const orderId = routeParams?.id ?? "";
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [expandedReceipt, setExpandedReceipt] = useState(false);
  const [openChatId, setOpenChatId] = useState<string | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  // The chat panel renders inline, so a trigger far down the page (e.g. the
  // "Open chat" button in the recent-messages card) would otherwise open it
  // off-screen and look like nothing happened. Scroll it into view.
  useEffect(() => {
    if (openChatId && chatRef.current) {
      chatRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [openChatId]);

  const { data, isLoading, error } = useQuery<OrderDetailResponse>({
    queryKey: ["/api/orders", orderId, "detail"],
    queryFn: async () => {
      const res = await fetch(`/api/orders/${orderId}/detail`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    enabled: Boolean(orderId),
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 15000,
  });

  const { data: review } = useQuery<{ review: { id: string } | null }>({
    queryKey: ["/api/reviews/order", orderId],
    queryFn: async () => {
      const res = await fetch(`/api/reviews/order/${orderId}`, {
        credentials: "include",
      });
      if (!res.ok) return { review: null };
      return res.json();
    },
    enabled: Boolean(orderId) && data?.viewerRole === "buyer",
  });

  const { data: messagesData } = useQuery<{
    messages: { id: string; senderName: string | null; body: string; createdAt: string | null }[];
    allowImages: boolean;
  }>({
    queryKey: ["/api/orders", orderId, "messages"],
    queryFn: async () => {
      const res = await fetch(`/api/orders/${orderId}/messages`, {
        credentials: "include",
      });
      if (!res.ok) return { messages: [], allowImages: false };
      return res.json();
    },
    enabled: Boolean(orderId),
    refetchInterval: 30000,
  });
  const recentMessages = messagesData?.messages;

  const statusMutation = useMutation({
    mutationFn: async (status: string) => {
      trackEvent("order_action_tap", `/orders/${orderId}`, {
        orderId,
        action: status,
        surface: "order_detail",
      });
      const res = await apiRequest("POST", `/api/orders/${orderId}/status`, {
        status,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/orders", orderId, "detail"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/my/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/seller/orders"] });
    },
    onError: (err: Error, status) => {
      trackEvent("order_action_fail", `/orders/${orderId}`, {
        orderId,
        action: status,
        surface: "order_detail",
        message: String(err?.message || "").slice(0, 300),
      });
      toast({
        title: "Couldn't update order",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const order = data?.order;
  const lines = data?.lines ?? [];
  const sellers = data?.sellers ?? [];
  const buyer = data?.buyer;
  const deliveryRequest = data?.deliveryRequest ?? null;
  const viewerRole: ViewerRole | undefined = data?.viewerRole;
  const status = String(order?.status || "").trim().toLowerCase();
  const isDelivery = isDeliveryMethod(order?.deliveryMethod);
  const isCommunityDelivery = order?.deliveryMethod === "community_delivery";

  const sellerMap = useMemo(() => {
    const map = new Map<string, DetailSeller>();
    for (const s of sellers) map.set(s.id, s);
    return map;
  }, [sellers]);

  const linesBySeller = useMemo(() => {
    const map = new Map<string, DetailLine[]>();
    for (const line of lines) {
      const sellerId = line.sellerId ?? "unknown";
      const existing = map.get(sellerId) ?? [];
      existing.push(line);
      map.set(sellerId, existing);
    }
    return map;
  }, [lines]);

  const subtotal = order ? parseFloat(order.subtotal) : 0;
  const platformFee = order ? parseFloat(order.platformFee) : 0;
  const deliveryFee = order?.deliveryFee ? parseFloat(order.deliveryFee) : 0;
  const total = order ? parseFloat(order.total) : 0;

  const sellerNet = useMemo(() => {
    if (!order || viewerRole !== "seller" || !user) return 0;
    const myLines = lines.filter((l) => l.sellerId === user.id);
    if (myLines.length === 0) return 0;
    const mySubtotal = myLines.reduce(
      (sum, l) => sum + parseFloat(l.unitPrice) * l.qty,
      0,
    );
    const splits = calculateMultiSellerFees([
      {
        sellerId: user.id,
        isFoundingMaker: sellerMap.get(user.id)?.isFoundingMaker ?? false,
        subtotal: mySubtotal,
      },
    ]);
    return splits.perSeller[0]?.sellerNet ?? mySubtotal;
  }, [order, viewerRole, user, lines, sellerMap]);

  const hasReview = Boolean(review?.review);

  const cta = useMemo(
    () =>
      viewerRole
        ? buildCta({
            viewerRole,
            status,
            isDelivery,
            isCommunityDelivery,
            hasReview,
            onStatusChange: (s) => statusMutation.mutate(s),
            onGoToDeliveries: () => setLocation("/deliveries"),
            onOpenReview: () => {
              const target = document.getElementById("order-review");
              if (target) target.scrollIntoView({ behavior: "smooth" });
            },
          })
        : null,
    [viewerRole, status, isDelivery, isCommunityDelivery, hasReview, statusMutation, setLocation],
  );

  if (!orderId) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="container mx-auto flex-1 px-4 py-8">
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              No order specified.
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="container mx-auto flex-1 px-4 py-8">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </main>
      </div>
    );
  }

  if (error || !data || !order || !buyer) {
    const status = (error as any)?.status as number | undefined;
    const message =
      status === 403
        ? "You don't have access to this order."
        : status === 404
          ? "Order not found."
          : "Couldn't load this order.";
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="container mx-auto flex-1 px-4 py-8">
          <Card>
            <CardContent className="py-10 text-center space-y-3">
              <p className="text-muted-foreground">{message}</p>
              <Button variant="outline" onClick={() => setLocation("/orders")}>
                Back to my orders
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const counterparties: { id: string; name: string; profileImageUrl: string | null }[] = [];
  if (viewerRole === "buyer") {
    const seen = new Set<string>();
    for (const line of lines) {
      if (!line.sellerId || line.sellerId === user?.id) continue;
      if (seen.has(line.sellerId)) continue;
      seen.add(line.sellerId);
      const seller = sellerMap.get(line.sellerId);
      counterparties.push({
        id: line.sellerId,
        name: stripDemoMarker(seller?.displayName ?? line.sellerName) || "Seller",
        profileImageUrl: seller?.profileImageUrl ?? null,
      });
    }
  } else if (viewerRole === "seller") {
    counterparties.push({
      id: buyer.id,
      name: buyer.displayName,
      profileImageUrl: buyer.profileImageUrl ?? null,
    });
  } else if (viewerRole === "driver") {
    if (buyer) {
      counterparties.push({
        id: buyer.id,
        name: buyer.displayName,
        profileImageUrl: buyer.profileImageUrl ?? null,
      });
    }
    // The driver coordinates the handoff with the seller(s) too, not just the
    // buyer — surface each seller as a contact.
    const seen = new Set<string>();
    for (const line of lines) {
      if (!line.sellerId || line.sellerId === user?.id) continue;
      if (seen.has(line.sellerId)) continue;
      seen.add(line.sellerId);
      const seller = sellerMap.get(line.sellerId);
      counterparties.push({
        id: line.sellerId,
        name: stripDemoMarker(seller?.displayName ?? line.sellerName) || "Seller",
        profileImageUrl: seller?.profileImageUrl ?? null,
      });
    }
  }

  const lastTwoMessages = (recentMessages ?? []).slice(-2);

  const timelineEvents: { key: string; label: string; at: string | Date | null }[] = [];
  if (order.createdAt)
    timelineEvents.push({ key: "placed", label: "Order placed", at: order.createdAt });
  for (const sel of order.fulfillmentSelections ?? []) {
    if (sel.scheduleConfirmedAt)
      timelineEvents.push({
        key: `schedule-${sel.id}`,
        label: `${stripDemoMarker(sel.sellerName) || sel.sellerName} confirmed schedule`,
        at: sel.scheduleConfirmedAt,
      });
    if (sel.readyConfirmedAt)
      timelineEvents.push({
        key: `ready-${sel.id}`,
        label: `${stripDemoMarker(sel.sellerName) || sel.sellerName} marked ready`,
        at: sel.readyConfirmedAt,
      });
  }
  if (deliveryRequest?.acceptedByUserId)
    timelineEvents.push({
      key: "driver-accepted",
      label: "Driver accepted",
      at: deliveryRequest.scheduledFor,
    });
  if (deliveryRequest?.completedAt)
    timelineEvents.push({
      key: "delivered",
      label: "Delivered",
      at: deliveryRequest.completedAt,
    });

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="container mx-auto flex-1 px-4 py-6">
        <div className="mx-auto max-w-2xl space-y-4">
          <Card>
            <CardContent className="pt-5 pb-5 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Order</p>
                  <p className="font-mono font-semibold text-lg">
                    #{order.id.slice(0, 8).toUpperCase()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Placed {timeAgo(order.createdAt)}
                  </p>
                </div>
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass(status)}`}
                  data-testid="text-order-status"
                >
                  {formatOrderStatus(status)}
                </span>
              </div>
            </CardContent>
          </Card>

          {counterparties.length > 0 && (
            <Card>
              <CardContent className="pt-5 pb-5 space-y-3">
                <p className="text-sm font-semibold">
                  {viewerRole === "buyer" ? "Seller" : viewerRole === "driver" ? "Contacts" : "Buyer"}
                </p>
                {counterparties.map((cp) => (
                  <div
                    key={cp.id}
                    className="flex items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {cp.profileImageUrl ? (
                        <img
                          src={cp.profileImageUrl}
                          alt=""
                          className="h-9 w-9 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center text-xs font-semibold text-muted-foreground">
                          {cp.name.slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <span className="truncate text-sm font-medium">{cp.name}</span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setOpenChatId((prev) => (prev === cp.id ? null : cp.id))
                      }
                    >
                      <MessageCircle className="mr-1.5 h-4 w-4" />
                      Message
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {openChatId && (
            <div ref={chatRef}>
              <OrderChat
                orderId={order.id}
                otherPartyName={
                  counterparties.find((c) => c.id === openChatId)?.name ??
                  (deliveryRequest?.driver?.id === openChatId
                    ? deliveryRequest.driver.displayName
                    : "")
                }
                recipientId={openChatId}
              />
            </div>
          )}

          <Card>
            <CardContent className="pt-5 pb-5 space-y-4">
              <p className="text-sm font-semibold">Items</p>
              {Array.from(linesBySeller.entries()).map(([sellerId, sellerLines]) => {
                const seller = sellerMap.get(sellerId);
                const sellerName =
                  stripDemoMarker(seller?.displayName ?? sellerLines[0]?.sellerName) ||
                  "Seller";
                return (
                  <div key={sellerId} className="space-y-2">
                    {linesBySeller.size > 1 && (
                      <p className="text-xs font-medium text-muted-foreground">
                        From {sellerName}
                      </p>
                    )}
                    {sellerLines.map((line) => {
                      const lineTotal =
                        parseFloat(line.unitPrice) * line.qty;
                      return (
                        <div
                          key={line.id}
                          className="flex items-center gap-3"
                          data-testid={`order-line-${line.id}`}
                        >
                          {line.imageUrl ? (
                            <img
                              src={line.imageUrl}
                              alt=""
                              className="h-14 w-14 rounded-md object-cover"
                            />
                          ) : (
                            <div className="h-14 w-14 rounded-md bg-muted flex items-center justify-center">
                              <Package className="h-5 w-5 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {stripDemoMarker(line.itemName) || line.itemName}
                            </p>
                            {line.unitWeightCentiLb ? (
                              <p className="text-xs text-muted-foreground">
                                {formatWeight(line.unitWeightCentiLb)} · ${parseFloat(line.unitPrice).toFixed(2)}
                              </p>
                            ) : (
                              <p className="text-xs text-muted-foreground">
                                {line.qty} × ${parseFloat(line.unitPrice).toFixed(2)}
                              </p>
                            )}
                            {linesBySeller.size === 1 && (
                              <p className="text-xs text-muted-foreground">
                                {stripDemoMarker(line.sellerName) || line.sellerName}
                              </p>
                            )}
                            {line.requestText && (
                              <p className="mt-1 rounded-md bg-primary/5 px-2 py-1 text-xs leading-5 text-foreground">
                                <span className="font-semibold text-primary">Request:</span>{" "}
                                {line.requestText}
                              </p>
                            )}
                            {line.requestImageUrls && line.requestImageUrls.length > 0 && (
                              <div className="mt-1">
                                <p className="mb-1 text-xs font-semibold text-primary">
                                  Reference photos
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                  {line.requestImageUrls.map((url) => (
                                    <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                                      <img
                                        src={url}
                                        alt="Reference"
                                        className="h-16 w-16 rounded-md border object-cover"
                                      />
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                            {line.customResponses && line.customResponses.length > 0 && (
                              <div className="mt-1 space-y-0.5 rounded-md bg-primary/5 px-2 py-1 text-xs leading-5 text-foreground">
                                {line.customResponses.map((r, i) => (
                                  <p key={i}>
                                    <span className="font-semibold text-primary">{r.label}:</span>{" "}
                                    {Array.isArray(r.value) ? r.value.join(", ") : r.value}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                          <p className="text-sm font-semibold">
                            ${lineTotal.toFixed(2)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-5 pb-5 space-y-3">
              <p className="text-sm font-semibold">
                {isDelivery ? "Delivery" : "Pickup"}
              </p>

              {isDelivery && deliveryRequest ? (
                <LiveDeliveryTracker orderId={order.id} />
              ) : null}

              {isDelivery ? (
                <>
                  {(viewerRole === "buyer" ||
                    viewerRole === "seller" ||
                    viewerRole === "driver") &&
                  (buyer.addressLine1 || deliveryRequest?.buyerAddressLine1) ? (
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-4 w-4 text-primary" />
                      <div className="text-sm">
                        <p>
                          {buyer.addressLine1 ?? deliveryRequest?.buyerAddressLine1}
                          {(buyer.addressLine2 || deliveryRequest?.buyerAddressLine2)
                            ? ` ${buyer.addressLine2 ?? deliveryRequest?.buyerAddressLine2}`
                            : ""}
                        </p>
                        <p className="text-muted-foreground">
                          {buyer.city ?? deliveryRequest?.buyerCity}
                          {(buyer.state ?? deliveryRequest?.buyerState)
                            ? `, ${buyer.state ?? deliveryRequest?.buyerState}`
                            : ""}
                          {buyer.zipCode ? ` ${buyer.zipCode}` : ""}
                        </p>
                      </div>
                    </div>
                  ) : null}

                  {deliveryRequest?.scheduledFor && (
                    <div className="rounded-md border border-primary/30 bg-primary/5 p-2 text-xs font-medium text-primary">
                      Driver scheduled: {formatScheduledFor(deliveryRequest.scheduledFor)}
                    </div>
                  )}

                  {deliveryRequest?.driver && (
                    <div className="flex items-center justify-between rounded-md border bg-muted/30 p-3">
                      <div className="flex items-center gap-3">
                        {deliveryRequest.driver.profileImageUrl ? (
                          <img
                            src={deliveryRequest.driver.profileImageUrl}
                            alt=""
                            className="h-10 w-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Truck className="h-5 w-5 text-primary" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium">
                            {deliveryRequest.driver.displayName}
                          </p>
                          <p className="text-xs text-muted-foreground">Driver</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          aria-label="Message driver"
                          onClick={() => setOpenChatId(deliveryRequest.driver!.id)}
                        >
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          aria-label="Call driver"
                          onClick={() => {
                            toast({
                              title: "Driver phone unavailable",
                              description:
                                "Use Message to reach the driver right now.",
                            });
                          }}
                        >
                          <Phone className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  {(order.fulfillmentSelections ?? []).length > 1 ? (
                    (order.fulfillmentSelections ?? []).map((sel) => (
                      <div
                        key={sel.id}
                        className="rounded-md bg-muted/40 p-3 text-sm space-y-1"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium">
                            {stripDemoMarker(sel.sellerName) || sel.sellerName}
                          </p>
                          <span
                            className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${
                              (sel as any).readyConfirmedAt
                                ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {(sel as any).readyConfirmedAt ? "Ready" : "Preparing"}
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <MapPin className="mt-0.5 h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-muted-foreground">
                            {sel.dropZoneName
                              ? `${sel.dropZoneName}${sel.dropZoneCity ? ` — ${sel.dropZoneCity}` : ""}${sel.dropZoneState ? `, ${sel.dropZoneState}` : ""}`
                              : sel.buyerLabel}
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <Calendar className="mt-0.5 h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-muted-foreground">
                            {sel.scheduledDate} · {sel.timeWindowLabel}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="flex items-start gap-2">
                        <MapPin className="mt-0.5 h-4 w-4 text-primary" />
                        <p className="text-sm text-muted-foreground">
                          {order.pickupLocation}
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <Calendar className="mt-0.5 h-4 w-4 text-primary" />
                        <p className="text-sm text-muted-foreground">
                          {order.pickupWindow}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-5 pb-5 space-y-2">
              {viewerRole === "seller" && !expandedReceipt ? (
                <button
                  type="button"
                  className="flex w-full items-center justify-between text-left"
                  onClick={() => setExpandedReceipt(true)}
                >
                  <div className="flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">Your net</span>
                  </div>
                  <span className="flex items-center gap-2">
                    <span className="text-base font-semibold">
                      ${sellerNet.toFixed(2)}
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </span>
                </button>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-4 w-4 text-primary" />
                      <span className="text-sm font-semibold">Receipt</span>
                    </div>
                    {viewerRole === "seller" && (
                      <button
                        type="button"
                        onClick={() => setExpandedReceipt(false)}
                        className="text-muted-foreground"
                        aria-label="Collapse receipt"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      Service fee
                      <ServiceFeeInfo subtotal={subtotal} />
                    </span>
                    <span>${platformFee.toFixed(2)}</span>
                  </div>
                  {deliveryFee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Delivery fee</span>
                      <span>${deliveryFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold border-t pt-2">
                    <span>Total</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                  {viewerRole === "seller" && (
                    <div className="flex justify-between text-sm pt-1">
                      <span className="text-muted-foreground">Your net</span>
                      <span className="font-semibold">
                        ${sellerNet.toFixed(2)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {cta && (
            <div className="sticky bottom-16 z-10 -mx-4 border-t bg-background/95 px-4 py-3 backdrop-blur sm:static sm:mx-0 sm:rounded-lg sm:border sm:bg-card">
              <Button
                type="button"
                size="lg"
                variant={cta.variant ?? "default"}
                className="w-full"
                onClick={() => {
                  if (statusMutation.isPending) {
                    toast({
                      title: "Working on it",
                      description: "Hang tight, your last action is processing.",
                    });
                    return;
                  }
                  cta.onClick();
                }}
                data-testid="button-order-cta"
              >
                {statusMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating…
                  </>
                ) : (
                  cta.label
                )}
              </Button>
            </div>
          )}

          {viewerRole === "buyer" &&
            ["paid", "preparing", "ready_for_pickup", "out_for_delivery", "picked_up", "delivered"].includes(
              status,
            ) &&
            (() => {
              const refundOnly = status === "picked_up" || status === "delivered";
              return (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    if (statusMutation.isPending) {
                      toast({
                        title: "Working on it",
                        description: "Hang tight, your last action is processing.",
                      });
                      return;
                    }
                    if (
                      window.confirm(
                        refundOnly
                          ? "Request a refund for this order? Your seller will be notified to review it."
                          : "Request to cancel this order and get a refund? Your seller will be notified to review it.",
                      )
                    ) {
                      statusMutation.mutate("cancel_requested");
                    }
                  }}
                  data-testid="button-request-cancellation"
                >
                  {refundOnly ? "Request refund" : "Request cancellation & refund"}
                </Button>
              );
            })()}

          {lastTwoMessages.length > 0 && counterparties.length > 0 && (
            <Card>
              <CardContent className="pt-5 pb-5 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Recent messages</p>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setOpenChatId(counterparties[0]?.id ?? null)}
                  >
                    Open chat
                  </Button>
                </div>
                {lastTwoMessages.map((m) => (
                  <div key={m.id} className="text-sm">
                    <p className="text-xs text-muted-foreground">
                      {m.senderName || "Someone"} · {timeAgo(m.createdAt)}
                    </p>
                    <p className="line-clamp-2">{m.body}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {viewerRole === "buyer" && !hasReview && (status === "picked_up" || status === "delivered") && (
            <Card id="order-review">
              <CardContent className="pt-5 pb-5 space-y-2">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-primary" />
                  <p className="text-sm font-semibold">Rate your order</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Reviews keep sellers honest and help your neighbors choose well.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setLocation(`/order-confirmation?orderId=${order.id}`)}
                >
                  Leave a review
                </Button>
              </CardContent>
            </Card>
          )}

          <details className="rounded-lg border bg-card px-4 py-3">
            <summary className="cursor-pointer text-sm font-semibold">
              Timeline
            </summary>
            <ol className="mt-3 space-y-2 text-sm">
              {timelineEvents.length === 0 && (
                <li className="text-muted-foreground">No events yet.</li>
              )}
              {timelineEvents.map((ev) => (
                <li key={ev.key} className="flex items-start justify-between gap-3">
                  <span>{ev.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {ev.at ? new Date(ev.at).toLocaleString() : ""}
                  </span>
                </li>
              ))}
            </ol>
          </details>
        </div>
      </main>
    </div>
  );
}
